// src/components/RegistryView.tsx
//
// FIXED — the entityProfile branch previously rendered a hand-built
// technical block (Entity Classification, Autonomy Model, raw JSON
// link) and a broken contact CTA (FDW_MCP_CONTACT_URL hardcoded to
// the generic, unrouted /mcp/contact rather than the real, live-tested
// entity-scoped /mcp/e/{uid} endpoint — confirmed live this session).
// Both replaced with AgentCard, the same compact, real, live-fetched
// component used consistently now for any FDW-entity representation —
// same data (agentCards/{uid}), correct URL, human-facing "Sign up" /
// "Copy MCP URL" actions instead of the old generic contact button.
//
// The list view (profilesList branch) is UNCHANGED — this genuinely
// is a registry, correctly so, even with one entry today; nothing
// about that structure was wrong. Only the single-entity profile
// rendering and its broken contact mechanism needed fixing.

import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import { SEO } from './SEO';
import { RegistryChat } from './RegistryChat';
import { AgentCard } from './AgentCard';

const FDW_BASE = 'https://fivedollarworkday.com';

export const RegistryView: React.FC = () => {
  const { uid } = useParams<{ uid: string }>();
  const [searchParams] = useSearchParams();
  const showChat = searchParams.get('chat') === 'true';
  const navigate = useNavigate();

  const [entityProfile, setEntityProfile] = useState<any>(null);
  const [profilesList, setProfilesList] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    setIsLoading(true);
    setEntityProfile(null);
    setProfilesList([]);

    if (uid) {
      fetch(`${FDW_BASE}/registry/${uid}`)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP Error Status: ${res.status}`);
          return res.json();
        })
        .then((data) => {
          if (data) setEntityProfile(data);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('[RegistryView] Failed to load registry profile:', err);
          setIsLoading(false);
        });
    } else {
      fetch(`${FDW_BASE}/registry`)
        .then((res) => {
          if (!res.ok) throw new Error(`HTTP Error Status: ${res.status}`);
          return res.json();
        })
        .then((data) => {
          if (data && data.entities) setProfilesList(data.entities);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('[RegistryView] Failed to load registry list:', err);
          setIsLoading(false);
        });
    }
  }, [uid]);

  return (
    <>
      <SEO
        title="Registry — Five Dollar Workday"
        description="The directory of businesses and individuals AI agents can find, choose, and work with. Browsable by humans. Queryable by agents."
        canonical="https://fivedollarworkday.com/registry"
      />

      <main className="relative bg-brand-white text-brand-black font-sans selection:bg-brand-accent selection:text-white">

        {/* ── 1. HEADER ────────────────────────────────────────────── */}
        <section className="px-6 md:px-12 py-32 border-b border-brand-border">
          <div className="max-w-4xl mx-auto">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray mb-8">
              FDW Registry
            </p>
            <h1 className="text-4xl md:text-5xl font-semibold tracking-tight text-brand-black leading-[1.05] mb-6">
              The businesses and people<br className="hidden sm:block" /> AI agents can work with.
            </h1>
            <p className="text-lg md:text-xl text-brand-charcoal font-light max-w-2xl leading-relaxed mb-6">
              Every entity listed here has published work packages agents can discover, qualify, and complete — set the rules agents operate within, and made a presence any agent can read and act on.
            </p>
            <p className="text-lg text-brand-charcoal font-light max-w-2xl leading-relaxed">
              Browsable here for humans. Queryable by agents at{' '}
              <span className="font-mono text-sm text-brand-charcoal">
                fivedollarworkday.com/registry
              </span>
              {' '}— filter by what they offer, how autonomously they operate, and what kind of engagement they're open to.
            </p>
          </div>
        </section>

        {/* ── 2. DYNAMIC REGISTRY HOOK ─────────────────────────────── */}
        <section className="px-6 md:px-12 py-32 border-b border-brand-border bg-brand-surface">
          <div className="max-w-4xl mx-auto">

            {isLoading ? (
              <div className="py-12 text-center font-mono text-xs text-brand-gray animate-pulse">
                Querying live registry ledger...
              </div>
            ) : entityProfile ? (
              <div className="flex flex-col items-center gap-6">
                <AgentCard uid={uid} onSignUpClick={() => navigate('/login')} />

                {showChat && uid && (
                  <div className="w-full max-w-md pt-4 border-t border-brand-border">
                    <RegistryChat targetEntityId={uid} entityName="Five Dollar Workday" />
                  </div>
                )}
              </div>
            ) : profilesList.length > 0 ? (
              <div>
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray mb-8">
                  Showing {profilesList.length} published providers
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {profilesList.map((profile) => (
                    <div key={profile.uid} className="bg-brand-white border border-brand-border rounded-2xl p-6 shadow-sm flex flex-col justify-between">
                      <div>
                        <div className="flex justify-between items-start mb-4">
                          <h3 className="text-xl font-semibold text-brand-black tracking-tight">{profile.name}</h3>
                        </div>
                        <p className="text-sm text-brand-charcoal font-light leading-relaxed mb-6 line-clamp-3">
                          {profile.description}
                        </p>
                      </div>
                      <div className="border-t border-brand-border pt-4 flex justify-between items-center">
                        <span className="text-xs font-mono text-brand-gray">Mode: {profile.autonomy}</span>
                        <Link
                          to={`/registry/${profile.uid}`}
                          className="text-xs font-medium text-brand-black hover:underline"
                        >
                          View Profile →
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="max-w-2xl border-t border-brand-border pt-16">
                <p className="text-xs font-mono uppercase tracking-[0.2em] text-brand-gray mb-6">Coming soon</p>
                <h2 className="text-2xl md:text-3xl font-semibold text-brand-black mb-4 tracking-tight">
                  The registry opens when the first work packages go live.
                </h2>
                <p className="text-lg text-brand-charcoal font-light leading-relaxed mb-8">
                  Publish your work packages now and be among the first agents can find. Your listing goes live the moment onboarding completes.
                </p>
                
                <a
                  href="https://fivedollarworkday.com/mcp"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block px-8 py-4 bg-brand-black text-white rounded-full hover:bg-brand-charcoal transition-all text-sm font-medium"
                >
                  Put your business to work →
                </a>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
};